import { IntegrationTemplate } from 'common/types/kraken-core/IntegrationTemplate'
import { useContext, useEffect, useState } from 'react'
import JsonForm from 'molecules/JsonForm/JsonForm'
import { Breadcrumbs, Button, Layout, Link } from '@loadsmart/loadsmart-ui'
import Modal, { useModal } from 'atoms/Modal'
import Loading from 'atoms/Loading'
import { TemplateOnboardContext } from './TemplateOnboardContext'
import TemplateActions from '../TemplateActions'
import useTopNavigationContext from 'hooks/useTopNavigationContext/useTopNavigationContext'

const DOUBLE_CHECK_MESSAGE = 'Are you sure you want to start the onboarding process?'
const CONFIRMED = 'Onboarding successfully started'
const FAILED = 'Confirmation failed'
const FOLLOW_ONBOARDING_MESSAGE = 'Follow the message resource for more details'

function TemplateOnboard() {
  const context = useContext(TemplateOnboardContext)
  const [confirmationModalHeaderTitle, setConfirmationModalHeaderTitle] = useState(
    DOUBLE_CHECK_MESSAGE
  )
  const [generatedData, setGeneratedData] = useState<any>({})
  const [confirmationWasConsented, setConfirmationConsent] = useState(false)
  const [successfullyConfirmed, setSuccessfullyConfirmed] = useState(false)
  const {
    id: confirmationModalId,
    open: confirmationModalIsOpen,
    toggle: toggleConfirmationModal,
  } = useModal({
    open: false,
    onOpen: () => {},
    onClose: () => {},
  })
  const topNavigationContext = useTopNavigationContext()

  const askForConfirmation = () => {
    if (!confirmationWasConsented) {
      toggleConfirmationModal()
    }
  }

  const confirm = async () => {
    if (context && context.confirmOnboard) {
      const wasSuccessfullyConfirmed = await context.confirmOnboard(generatedData)
      setSuccessfullyConfirmed(wasSuccessfullyConfirmed)
      if (!wasSuccessfullyConfirmed) setConfirmationConsent(false)
    }
  }

  const clear = () => {
    setGeneratedData({})
  }

  function FallbackComponent() {
    return (
      <>
        {context?.isLoading && <Loading />}
        {!context?.isLoading && <>Some error happened</>}
      </>
    )
  }

  function ConfirmationModal() {
    return (
      <Modal id={confirmationModalId} open={confirmationModalIsOpen}>
        <Modal.Header>{confirmationModalHeaderTitle}</Modal.Header>
        <Modal.Body>
          <div className="flex flex-row justify-center w-full">
            {context?.isLoading && <Loading />}
            {!context?.isLoading && !successfullyConfirmed && (
              <div className="flex">
                <div className="flex p-2">
                  <Button
                    onClick={() => {
                      toggleConfirmationModal()
                    }}
                  >
                    Cancel
                  </Button>
                </div>
                <div className="flex p-2">
                  <Button
                    variant="primary"
                    onClick={() => {
                      setConfirmationConsent(true)
                      confirm()
                    }}
                  >
                    Confirm
                  </Button>
                </div>
              </div>
            )}
            {!context?.isLoading && successfullyConfirmed && (
              <div className="flex flex-col items-center">
                <div className="flex">
                  <Link href={`/messages/${context.confirmationResponse?.messageId}`}>
                    {FOLLOW_ONBOARDING_MESSAGE}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    )
  }

  const OnboardForm = (template: IntegrationTemplate) => {
    const schema = template.schema || {}
    const uiSchema = template.ui_schema || undefined

    return (
      <div className="w-full">
        <JsonForm
          key="json-form"
          schema={schema}
          data={generatedData}
          uiSchema={uiSchema || undefined}
          onChange={({ data }) => {
            setGeneratedData(data)
          }}
        />
      </div>
    )
  }

  function OnboardComponent(template: IntegrationTemplate) {
    return (
      <>
        {context?.isLoading && <Loading className="pt-4" />}
        {!context?.isLoading && (
          <>
            <ConfirmationModal />

            <div className="flex flex-col w-full">
              <div className="flex w-full pt-4">{OnboardForm(template)}</div>
              {!context?.isLoading && (
                <TemplateActions
                  template={template}
                  clearFunction={clear}
                  generatedData={generatedData}
                  setGeneratedData={setGeneratedData}
                  askForConfirmationFunction={askForConfirmation}
                />
              )}
            </div>
          </>
        )}
      </>
    )
  }

  useEffect(() => {
    if (!confirmationWasConsented) {
      return
    }

    if (successfullyConfirmed) {
      setConfirmationModalHeaderTitle(CONFIRMED)
    } else {
      setConfirmationModalHeaderTitle(FAILED)
    }
  }, [confirmationWasConsented, successfullyConfirmed])

  useEffect(() => {
    topNavigationContext.updateState({
      children: (
        <Layout.Group className="w-full">
          <Breadcrumbs
            entries={[
              {
                label: 'Integration Onboarding',
                active: true,
                url: '/integration/onboarding',
              },
              {
                label: 'Onboarding',
                active: true,
              },
              {
                label: context.template?.key || '',
                active: true,
              },
            ]}
          />
        </Layout.Group>
      ),
    })
  }, [context.template])

  let component = FallbackComponent()
  if (context && context.template) {
    const { template } = context
    component = OnboardComponent(template)
  }

  return component
}

export default TemplateOnboard
